// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { Locale } from "@/i18n"

/**
 * The language the SERVER answers in — distinct from the dictionary the client
 * renders in, and easy to confuse with it.
 *
 * The API localises catalogue names itself (`ingredient.names[locale]`, chosen
 * from `users.locale`), so a Kitchen or Shop row's `displayName` arrives in
 * whatever language that column holds. Until 2026-09-10 nothing ever wrote that
 * column after onboarding: the in-app toggle swapped the client dictionary
 * alone, and an account created in EN and later switched to DE kept receiving
 * English ingredient names under German chrome, permanently.
 *
 * `PATCH /v1/me` is now the write path. Two things have to happen when the user
 * switches language, in this order:
 *
 *  1. the server is told (so later reads come back in the new language), and
 *  2. the surfaces that render server-localised names re-read.
 *
 * This module owns (2): a deliberately tiny notifier that only the surfaces
 * showing catalogue-backed names subscribe to. It is NOT the Amy data bus —
 * that one means "Amy changed your data", which is a different claim.
 */

type Listener = () => void
const listeners = new Set<Listener>()

/** Subscribe a surface that renders server-localised catalogue names. Returns an unsubscribe. */
export function subscribeServerLanguageChanged(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify(): void {
  for (const listener of [...listeners]) listener()
}

interface LocaleWriter {
  patchMe: (body: { locale: Locale }) => Promise<unknown>
}

/**
 * Persist `locale` as the caller's server language, then tell the catalogue
 * surfaces to re-read — but only once the write has actually landed, so nobody
 * refetches into the old language.
 *
 * A failed write degrades honestly: the chrome is already in the new language
 * and catalogue names stay as the server has them. It is never retried in a
 * loop and never blocks the UI.
 */
export async function syncServerLanguage(api: LocaleWriter, locale: Locale): Promise<void> {
  try {
    await api.patchMe({ locale })
  } catch {
    return
  }
  notify()
}
